import type {
  CmmdUserForgeAutomationRule,
  CmmdUserForgeAutomationRun,
} from "@cmmd-forge/contracts";

import { formatRelativeTimeLabel } from "~/timestampFormat";
import type { LocalAutomation, LocalAutomationRun } from "./localAutomationClient";

export type AutomationListItem =
  | {
      readonly kind: "cloud";
      readonly key: string;
      readonly automation: CmmdUserForgeAutomationRule;
      readonly runs: readonly CmmdUserForgeAutomationRun[];
    }
  | {
      readonly kind: "local";
      readonly key: string;
      readonly automation: LocalAutomation;
      readonly runs: readonly LocalAutomationRun[];
    };

export function automationRouteId(item: AutomationListItem): string {
  return `${item.kind}-${item.automation.id}`;
}

export function itemName(item: AutomationListItem): string {
  return item.automation.name;
}

export function itemPrompt(item: AutomationListItem): string {
  return item.automation.prompt;
}

export function itemStatus(item: AutomationListItem): string {
  return item.automation.status;
}

export function itemProjectName(
  item: AutomationListItem,
  projectNames: ReadonlyMap<string, string>,
): string {
  if (item.kind === "local") return item.automation.projectName ?? "Local project";
  return (
    projectNames.get(String(item.automation.projectId)) ?? `Project ${item.automation.projectId}`
  );
}

export function itemLocationLabel(item: AutomationListItem): string {
  return item.kind === "cloud" ? "Forge Cloud" : "This device";
}

export function itemTriggerLabel(item: AutomationListItem): string {
  if (item.kind === "cloud") {
    return (
      item.automation.scheduleDescription ??
      (item.automation.cronExpression ? "Scheduled" : "Manual")
    );
  }
  if (item.automation.scheduleKind === "manual") return "Manual";
  if (item.automation.scheduleKind === "every_minute") return "Every minute";
  if (item.automation.scheduleKind === "daily") {
    return `Daily at ${item.automation.scheduleTime ?? "09:00"}`;
  }
  const day = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"][
    item.automation.scheduleDayOfWeek ?? 1
  ];
  return `${day} at ${item.automation.scheduleTime ?? "09:00"}`;
}

function itemLastRunAt(item: AutomationListItem): string | null {
  if (item.kind === "cloud") return item.automation.lastRunAt;
  return item.automation.lastRunAt;
}

export function itemNextRunAt(item: AutomationListItem): string | null {
  return item.automation.nextRunAt;
}

export function itemLatestRunLabel(item: AutomationListItem): string {
  const lastRunAt = itemLastRunAt(item);
  return lastRunAt ? formatRelativeTimeLabel(lastRunAt) : "Never";
}
